"use client";

import React, { useEffect } from "react";
import "@/assets/stylesheets/application.scss";
import Header from "@/components/layouts/Header";
import Footer from "@/components/layouts/Footer";
import GoogleTagManager from "@/components/application/tracking/GoogleTagManager";
import GoogleTagManagerNoscript from "@/components/application/tracking/GoogleTagManagerNoscript";
// import Intercom from "@/components/application/Intercom";
import SchemaMarkup from "@/components/application/SchemaMarkup";
// import AzureAppInsights from "@/components/application/tracking/AzureAppInsights";
import StickySubnav from "@/components/layouts/StickySubnav";
import { logger } from '@/lib/logger';
import {
  FooterTargetDefense,
  HeaderTargetDefense,
  StickynavTargetDefense,
} from "@/.storyblok/types/319751/storyblok-components";
import { usePathname } from "next/navigation";
// import OneTrust from "@/components/application/tracking/OneTrust";
import Outfunnel from "@/components/application/tracking/Outfunnel";
import Mailchimp from "@/components/application/tracking/Mailchimp";
import WhatConverts from "@/components/application/tracking/WhatConverts";
import { CustomScriptRenderer } from "@/components/CustomScriptRenderer";
import { SbBlokData } from "@storyblok/react";

const stylesheets = {
  // @ts-expect-error - SCSS module import not recognized by TypeScript
  articles: () => import('@/assets/stylesheets/articles.scss'),
  // @ts-expect-error - SCSS module import not recognized by TypeScript
  leadership: () => import('@/assets/stylesheets/leadership.scss'),
  // @ts-expect-error - SCSS module import not recognized by TypeScript
  penetrationTestingService: () => import('@/assets/stylesheets/penetration-testing-service.scss'),
} as const;

interface ApplicationLayoutProps {
  children: React.ReactNode;
  heroContent?: React.ReactNode;
  styleController?: string;
  pageTitle?: string;
  pageDescription?: string;
  pageImage?: string;
  metadata?: any;
  noIndex?: boolean;
  adLandingPage?: boolean;
  header?: HeaderTargetDefense & SbBlokData;
  footer?: FooterTargetDefense & SbBlokData;
  stickynav?:StickynavTargetDefense & SbBlokData;
  scripts?: any;
  schemaMarkupBlog?: any;
  schemaMarkupEvent?: any;
  schemaMarkupProduct?: any;
}

const ApplicationLayout: React.FC<ApplicationLayoutProps> = ({
  children,
  heroContent,
  styleController = "",
  pageTitle = "TargetDefense.com",
  pageDescription = "Expert US cybersecurity & compliance services help you keep the bad guy out, meet compliance, & protect your critical data.",
  pageImage = "/social-markup/default-social.png",
  noIndex,
  adLandingPage = false,
  metadata,
  header,
  footer,
  stickynav,
  scripts,
  schemaMarkupBlog,
  schemaMarkupEvent,
  schemaMarkupProduct,
}) => {
  const pathname = usePathname();
  const canonicalUrl = process.env.NEXT_PUBLIC_BASE_URL
    ? `${process.env.NEXT_PUBLIC_BASE_URL}${pathname}`
    : `http://localhost:3000${pathname}`;
  const usBaseUrl = "https://www.targetdefense.com";
  const ukBaseUrl = "https://www.bulletproof.co.uk";
  const localizedPath = pathname === "/" ? "/" : pathname;
  const usAlternateUrl = `${usBaseUrl}${localizedPath}`;
  const ukAlternateUrl = `${ukBaseUrl}${localizedPath}`;
  // const isHomePage = pathname === "/";

  const title = metadata?.title || pageTitle;
  const siteTitle = "TargetDefense.com";
  const fullTitle = title?.toLowerCase().includes(siteTitle.toLowerCase())
    ? title
    : `${title} – ${siteTitle}`;
  const description = metadata?.description || pageDescription;
  const ogTitle = metadata?.openGraph?.title || title;
  const ogDescription = metadata?.openGraph?.description || description;
  const ogImage = metadata?.openGraph?.images?.[0]?.url || pageImage;

  useEffect(() => {
    const loadStylesheet = async () => {
      try {
        if (pathname === '/leadership') {
          await stylesheets.leadership();
        } else if (pathname.startsWith('/articles')) {
          await stylesheets.articles();
        } else if (pathname === '/penetration-testing-service' ||
          pathname === '/penetration-testing-services') {
          await stylesheets.penetrationTestingService();
        }
      } catch (error) {
        logger.error('Error loading stylesheet:', error);
      }
    };

    loadStylesheet();
  }, [pathname]);

  return (
    <html className="no-js" lang="en-us">
      {/* eslint-disable-next-line @next/next/no-head-element */}
      <head>
        {/* {isHomePage && <OneTrust />} */}

        <GoogleTagManager />
        <title>{fullTitle}</title>
        <meta name="description" content={description} />
        <link
          rel="preload"
          href="/assets/images/header-backgrounds/homepage.jpg"
          as="image"
          type="image/jpeg"
        />

        <link rel="preconnect" href="https://www.googletagmanager.com" />

        {/* Open Graph Meta Tags */}
        <meta property="og:site_name" content="TargetDefense.com" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta
          property="og:type"
          content={metadata?.openGraph?.type || "website"}
        />
        <meta
          property="og:url"
          content={metadata?.openGraph?.url || canonicalUrl}
        />
        <meta property="og:image" content={ogImage} />

        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta
          name="ahrefs-site-verification"
          content="5d88f354cdf5da2b0f22b6b94d139c4ea610277299b2b4bb70cf00eca8352160"
        />

        {noIndex && <meta name="robots" content="noindex, nofollow" />}

        <link rel="icon" type="image/svg+xml" href="/favicon.svg" />
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        <link rel="mask-icon" href="/safari-pinned-tab.svg" color="#5bbad5" />
        <link rel="canonical" href={canonicalUrl} />
        <link rel="alternate" hrefLang="en-US" href={usAlternateUrl} />
        <link rel="alternate" hrefLang="en-GB" href={ukAlternateUrl} />
        <link rel="alternate" hrefLang="x-default" href={usAlternateUrl} />
        <meta property="og:locale" content="en_US" />
        <meta name="geo.region" content="US" />
        <meta name="geo.placename" content="United States" />
        <meta name="msapplication-TileColor" content="#324450" />
        <meta name="theme-color" content="#324450" />

        <script type="application/ld+json" defer>
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: pageTitle,
            description: pageDescription,
            image: pageImage,
            url: canonicalUrl,
          })}
        </script>

        {!noIndex && (
          <SchemaMarkup
            blogPost={schemaMarkupBlog}
            eventDetails={schemaMarkupEvent}
            productDetails={schemaMarkupProduct}
          />
        )}
        {/*<AzureAppInsights />*/}
        <Outfunnel />
        <Mailchimp />
      </head>

      <body className={styleController}>
        {scripts && <CustomScriptRenderer customScriptBlock={scripts} />}
        <GoogleTagManagerNoscript />
        <WhatConverts />

        {stickynav && <StickySubnav blok={stickynav} />}

        <header>
          <Header
            heroContent={heroContent}
            blok={header}
            headerClass={styleController}
          />
        </header>

        <main>{children}</main>

        {!adLandingPage && (
          <footer>{footer && <Footer blok={footer} />}</footer>
        )}

        {/*<Intercom />*/}
      </body>
    </html>
  );
};

export default ApplicationLayout;
